import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { toast } from 'sonner';
import {
  CheckCircle, ChevronDown, ChevronRight, Search, Bookmark, ArrowRight,
  Building2, GraduationCap, Zap, Info, MapPin, Check, Briefcase,
} from 'lucide-react';

const CareerPreferences = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // === Career Discovery state ===
  const [industries, setIndustries] = useState([]);
  const [expandedIndustry, setExpandedIndustry] = useState(null);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // === Preferences (location/company) state ===
  const [counties, setCounties] = useState([]);
  const [countiesWithEmployers, setCountiesWithEmployers] = useState([]);
  const [employerDirectory, setEmployerDirectory] = useState({});
  const [selectedCounty, setSelectedCounty] = useState('');
  const [countyCompanies, setCountyCompanies] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // === Shared UI state ===
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [quickRunning, setQuickRunning] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [savedSelections, setSavedSelections] = useState({ industries: [], roles: [] });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [industriesRes, discoveryStatusRes, prefOptionsRes, prefDataRes] = await Promise.all([
        axios.get(`${API_BASE}/career-discovery/industries`),
        axios.get(`${API_BASE}/career-discovery/status/${user.id}`),
        axios.get(`${API_BASE}/preferences/options`),
        axios.get(`${API_BASE}/preferences/data/${user.id}`),
      ]);

      setIndustries(industriesRes.data.industries);

      const dir = prefOptionsRes.data.employer_directory || {};
      setCounties(prefOptionsRes.data.counties || []);
      setCountiesWithEmployers(prefOptionsRes.data.counties_with_employers || []);
      setEmployerDirectory(dir);

      const discoveryDone = discoveryStatusRes.data.completed;
      const prefsDone = prefDataRes.data.completed;

      if (discoveryDone) {
        setSelectedIndustries(discoveryStatusRes.data.selected_industries || []);
        setSelectedRoles(discoveryStatusRes.data.selected_roles || []);
        setSavedSelections({
          industries: discoveryStatusRes.data.selected_industries || [],
          roles: discoveryStatusRes.data.selected_roles || [],
        });
      }
      if (prefsDone) {
        const savedCounty = prefDataRes.data.county;
        setSelectedCounty(savedCounty);
        setCountyCompanies(dir[savedCounty] || []);
        setPriorities(prefDataRes.data.company_priorities || []);
      }
      if (discoveryDone && prefsDone) {
        setCompleted(true);
      }
    } catch (error) {
      console.error('Failed to fetch career preferences data:', error);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // === Discovery handlers ===
  const toggleIndustry = (industryId) => {
    setExpandedIndustry(prev => prev === industryId ? null : industryId);
  };
  const toggleIndustrySelection = (industryId) => {
    setSelectedIndustries(prev =>
      prev.includes(industryId) ? prev.filter(i => i !== industryId) : [...prev, industryId]
    );
  };
  const toggleRoleSelection = (roleId) => {
    setSelectedRoles(prev =>
      prev.includes(roleId) ? prev.filter(r => r !== roleId) : [...prev, roleId]
    );
  };

  // === Preferences handlers ===
  const handleSelectCounty = (county) => {
    setSelectedCounty(county);
    setDropdownOpen(false);
    const companies = employerDirectory[county] || [];
    setCountyCompanies(companies);
    setPriorities([]);
  };
  const toggleCompany = (compId) => {
    if (priorities.includes(compId)) {
      setPriorities(priorities.filter(id => id !== compId));
    } else if (priorities.length < 5) {
      setPriorities([...priorities, compId]);
    } else {
      toast.error('You can only select up to 5 companies');
    }
  };
  const moveUp = (idx) => {
    if (idx === 0) return;
    const updated = [...priorities];
    [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
    setPriorities(updated);
  };
  const moveDown = (idx) => {
    if (idx === priorities.length - 1) return;
    const updated = [...priorities];
    [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
    setPriorities(updated);
  };

  // === Save both ===
  const handleSaveAll = async () => {
    if (selectedRoles.length === 0 && selectedIndustries.length === 0) {
      toast.error('Please select at least one industry or role');
      return;
    }
    if (!selectedCounty) {
      toast.error('Please select a preferred county');
      return;
    }
    if (priorities.length === 0) {
      toast.error('Please rank at least one employer');
      return;
    }
    setSubmitting(true);
    try {
      await Promise.all([
        axios.post(`${API_BASE}/career-discovery/save-selections`, {
          user_id: user.id,
          selected_industries: selectedIndustries,
          selected_roles: selectedRoles,
        }),
        axios.post(`${API_BASE}/preferences/save/${user.id}`, {
          county: selectedCounty,
          company_priorities: priorities,
        }),
      ]);
      toast.success('Career preferences saved!');
      setSavedSelections({ industries: selectedIndustries, roles: selectedRoles });
      setCompleted(true);
    } catch (err) {
      toast.error('Failed to save preferences');
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickComplete = async () => {
    setQuickRunning(true);
    try {
      await Promise.all([
        axios.post(`${API_BASE}/demo/quick-complete-discovery/${user.id}`),
        axios.post(`${API_BASE}/demo/quick-complete-preferences/${user.id}`),
      ]);
      toast.success('Career Preferences auto-completed!');
      await fetchData();
    } catch (error) {
      toast.error('Quick complete failed');
    } finally {
      setQuickRunning(false);
    }
  };

  const filteredIndustries = searchQuery
    ? industries.filter(ind =>
        ind.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ind.roles.some(r => r.title.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : industries;

  const getCompany = (id) => countyCompanies.find(c => c.id === id);

  if (loading) {
    return (
      <div className="p-12 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0071e3]" />
      </div>
    );
  }

  // =========== COMPLETED VIEW ===========
  if (completed) {
    const savedIndustryNames = industries
      .filter(i => savedSelections.industries.includes(i.id))
      .map(i => i.name);
    const savedRoleNames = industries
      .flatMap(i => i.roles)
      .filter(r => savedSelections.roles.includes(r.id))
      .map(r => r.title);

    return (
      <div className="p-4 sm:p-8 md:p-12">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl sm:text-5xl font-semibold text-[#1d1d1f] mb-3" data-testid="career-preferences-complete-title">
              Career Preferences Complete
            </h1>
            <p className="text-lg text-[#86868b]">Your industries, roles, county, and company priorities have been saved.</p>
          </div>

          {savedIndustryNames.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 mb-4">
              <h3 className="text-lg font-semibold text-[#1d1d1f] mb-3 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#0071e3]" /> Industries of Interest
              </h3>
              <div className="flex flex-wrap gap-2">
                {savedIndustryNames.map(name => (
                  <span key={name} className="px-3 py-1.5 bg-blue-50 text-[#0071e3] rounded-full text-sm font-medium">
                    {name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {savedRoleNames.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 mb-4">
              <h3 className="text-lg font-semibold text-[#1d1d1f] mb-3 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-[#34C759]" /> Roles of Interest
              </h3>
              <div className="flex flex-wrap gap-2">
                {savedRoleNames.map(name => (
                  <span key={name} className="px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                    {name}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 mb-6">
            <h3 className="text-lg font-semibold text-[#1d1d1f] mb-3 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#0071e3]" /> Location & Company Priorities
            </h3>
            <div className="mb-3">
              <span className="text-sm text-[#86868b]">Preferred County: </span>
              <span className="text-sm font-semibold text-[#1d1d1f]">{selectedCounty}</span>
            </div>
            <ol className="space-y-2">
              {priorities.map((id, idx) => {
                const co = getCompany(id);
                return co ? (
                  <li key={id} className="flex items-center gap-3 text-sm" data-testid={`priority-result-${idx}`}>
                    <span className="w-6 h-6 rounded-full bg-[#0071e3] text-white flex items-center justify-center text-xs font-bold">{idx + 1}</span>
                    <span className="font-medium text-[#1d1d1f]">{co.name}</span>
                    <span className="text-[#86868b]">{co.sector}</span>
                  </li>
                ) : null;
              })}
            </ol>
          </div>

          <div className="bg-green-50 rounded-2xl border border-green-200 p-6 text-center">
            <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <h3 className="text-xl font-semibold text-[#1d1d1f] mb-1">Preferences Saved</h3>
            <p className="text-sm text-[#86868b] mb-4">Continue to Aptitude Tests to take your career assessments.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={() => setCompleted(false)}
                variant="outline"
                className="rounded-full border-[#0071e3] text-[#0071e3] px-8"
                data-testid="edit-career-preferences-btn"
              >
                Edit selections
              </Button>
              <Button
                onClick={() => navigate('/apprentice/assessments')}
                className="bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full px-8"
                data-testid="go-to-testing-btn"
              >
                Continue to Aptitude Tests <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // =========== FORM VIEW ===========
  const discoveryHasSelection = selectedIndustries.length > 0 || selectedRoles.length > 0;
  const prefsHasSelection = selectedCounty && priorities.length > 0;

  return (
    <div className="p-4 sm:p-8 md:p-12">
      <div className="max-w-5xl mx-auto">
        {/* Demo Quick Complete Banner */}
        <div className="bg-[#FFF8E1] border border-[#FFE082] rounded-2xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="career-preferences-demo-banner">
          <div className="text-sm text-[#1d1d1f]">
            <span className="font-semibold">Investor Demo:</span> Skip to see completed career preferences instantly
          </div>
          <Button
            onClick={handleQuickComplete}
            disabled={quickRunning}
            className="bg-[#FF9500] hover:bg-[#E68600] text-white rounded-full px-5 text-sm"
            data-testid="quick-complete-career-preferences-btn"
          >
            <Zap className="w-3.5 h-3.5 mr-1.5" />
            {quickRunning ? 'Completing...' : 'Quick Complete'}
          </Button>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-semibold text-[#1d1d1f] mb-3" data-testid="career-preferences-title">
            Career Preferences
          </h1>
          <p className="text-lg text-[#86868b]">
            Choose the industries and roles you're interested in, then tell us where you'd like to work and which employers excite you most.
          </p>
        </div>

        {/* ===================== SECTION 1: INDUSTRIES & ROLES ===================== */}
        <div className="mb-10">
          <div className="mb-4 flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-[#0071e3] text-white text-sm font-semibold flex items-center justify-center">1</span>
            <h2 className="text-2xl font-semibold text-[#1d1d1f]">Industries & Roles</h2>
          </div>
          <p className="text-sm text-[#86868b] mb-5 ml-9">
            Explore 16 industries and 80+ apprenticeship roles across Ireland. Select the ones that interest you.
          </p>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="w-5 h-5 text-[#86868b] absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search industries or roles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-[#d2d2d7] rounded-xl text-[#1d1d1f] placeholder:text-[#86868b] focus:outline-none focus:ring-2 focus:ring-[#0071e3] focus:border-transparent"
              data-testid="career-search-input"
            />
          </div>

          {/* Selection summary */}
          {discoveryHasSelection && (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6">
              <div className="text-sm text-[#1d1d1f]">
                <span className="font-semibold">{selectedIndustries.length}</span> industries and <span className="font-semibold">{selectedRoles.length}</span> roles selected
              </div>
            </div>
          )}

          {/* Industry cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {filteredIndustries.map((industry) => {
              const isExpanded = expandedIndustry === industry.id;
              const isIndustrySelected = selectedIndustries.includes(industry.id);
              return (
                <div
                  key={industry.id}
                  className={`bg-white rounded-2xl border transition-all ${
                    isIndustrySelected ? 'border-[#0071e3] shadow-sm' : 'border-[#d2d2d7]'
                  }`}
                  data-testid={`industry-${industry.id}`}
                >
                  <div
                    className="flex items-center justify-between p-5 cursor-pointer"
                    onClick={() => toggleIndustry(industry.id)}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleIndustrySelection(industry.id); }}
                        className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          isIndustrySelected ? 'border-[#0071e3] bg-[#0071e3]' : 'border-[#d2d2d7] hover:border-[#86868b]'
                        }`}
                        data-testid={`select-industry-${industry.id}`}
                      >
                        {isIndustrySelected && <CheckCircle className="w-4 h-4 text-white" />}
                      </button>
                      <div className="min-w-0">
                        <h3 className="text-base sm:text-lg font-semibold text-[#1d1d1f] truncate">{industry.name}</h3>
                        <span className="text-xs text-[#86868b]">{industry.role_count} apprenticeship{industry.role_count !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    {isExpanded
                      ? <ChevronDown className="w-5 h-5 text-[#86868b] flex-shrink-0" />
                      : <ChevronRight className="w-5 h-5 text-[#86868b] flex-shrink-0" />
                    }
                  </div>

                  {isExpanded && (
                    <div className="px-5 pb-5 border-t border-[#f5f5f7]">
                      <div className="grid sm:grid-cols-2 gap-2 pt-4">
                        {industry.roles.map((role) => {
                          const isRoleSelected = selectedRoles.includes(role.id);
                          return (
                            <div key={role.id} className="relative">
                              <button
                                onClick={() => toggleRoleSelection(role.id)}
                                className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                                  isRoleSelected
                                    ? 'border-[#0071e3] bg-blue-50'
                                    : 'border-[#d2d2d7] hover:border-[#86868b] bg-white'
                                }`}
                                data-testid={`role-${role.id}`}
                              >
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                  isRoleSelected ? 'border-[#0071e3] bg-[#0071e3]' : 'border-[#d2d2d7]'
                                }`}>
                                  {isRoleSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-medium text-[#1d1d1f] truncate">{role.title}</div>
                                  <div className="text-xs text-[#86868b]">{role.level}</div>
                                </div>
                                {role.description && (
                                  <div className="relative group/tip flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                    <Info className="w-4 h-4 text-[#b0b0b5] group-hover/tip:text-[#86868b] transition-colors" />
                                    <div className="absolute z-30 right-0 bottom-full mb-2 w-64 hidden group-hover/tip:block">
                                      <div className="bg-[#1d1d1f] text-white rounded-xl p-4 shadow-lg text-sm">
                                        <div className="font-semibold mb-1">{role.title}</div>
                                        <div className="text-[#a1a1a6] text-xs leading-relaxed">{role.description}</div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ===================== SECTION 2: LOCATION & COMPANY ===================== */}
        <div className="mb-10">
          <div className="mb-4 flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-[#0071e3] text-white text-sm font-semibold flex items-center justify-center">2</span>
            <h2 className="text-2xl font-semibold text-[#1d1d1f]">Location & Companies</h2>
          </div>
          <p className="text-sm text-[#86868b] mb-5 ml-9">
            Choose your preferred county to see available employers, then rank your top 5.
          </p>

          {/* County Selector */}
          <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 mb-6">
            <h3 className="text-lg font-semibold text-[#1d1d1f] mb-1 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#0071e3]" />
              Preferred County
            </h3>
            <p className="text-sm text-[#86868b] mb-4">Where would you like to complete your apprenticeship?</p>

            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-full flex items-center justify-between px-4 py-3 border border-[#d2d2d7] rounded-xl bg-white text-left hover:border-[#86868b] transition-colors"
                data-testid="county-selector"
              >
                <span className={selectedCounty ? 'text-[#1d1d1f] font-medium' : 'text-[#86868b]'}>
                  {selectedCounty || 'Select a county...'}
                </span>
                <ChevronDown className={`w-4 h-4 text-[#86868b] transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {dropdownOpen && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-[#d2d2d7] rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  {counties.map((county) => {
                    const hasEmployers = countiesWithEmployers.includes(county);
                    const count = (employerDirectory[county] || []).length;
                    return (
                      <button
                        key={county}
                        onClick={() => handleSelectCounty(county)}
                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#f5f5f7] transition-colors flex items-center justify-between ${
                          selectedCounty === county ? 'bg-blue-50 text-[#0071e3] font-medium' : 'text-[#1d1d1f]'
                        }`}
                        data-testid={`county-option-${county}`}
                      >
                        <span>{county}</span>
                        {hasEmployers && (
                          <span className="text-xs text-[#86868b] bg-[#f5f5f7] px-2 py-0.5 rounded-full">{count} {count === 1 ? 'employer' : 'employers'}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Company List */}
          {selectedCounty && (
            <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 mb-6">
              <h3 className="text-lg font-semibold text-[#1d1d1f] mb-1 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#34C759]" />
                Employers in {selectedCounty}
              </h3>

              {countyCompanies.length === 0 ? (
                <div className="mt-4 text-center py-8">
                  <Building2 className="w-10 h-10 text-[#d2d2d7] mx-auto mb-3" />
                  <p className="text-sm text-[#86868b]">No employers currently registered in {selectedCounty}.</p>
                  <p className="text-xs text-[#86868b] mt-1">As employers join the platform, they'll appear here automatically.</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-[#86868b] mb-4">
                    Click to select your top 5 preferred employers ({priorities.length}/5 selected).
                  </p>

                  {priorities.length > 0 && (
                    <div className="mb-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <p className="text-xs font-semibold text-[#0071e3] mb-2">Your Ranked Selections</p>
                      <div className="space-y-1.5">
                        {priorities.map((id, idx) => {
                          const co = getCompany(id);
                          if (!co) return null;
                          return (
                            <div key={id} className="flex items-center gap-2 text-sm" data-testid={`ranked-${idx}`}>
                              <span className="w-5 h-5 rounded-full bg-[#0071e3] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{idx + 1}</span>
                              <span className="font-medium text-[#1d1d1f] flex-1">{co.name}</span>
                              <div className="flex gap-1">
                                <button onClick={() => moveUp(idx)} disabled={idx === 0} className="w-5 h-5 text-[10px] rounded border border-[#d2d2d7] text-[#86868b] hover:text-[#1d1d1f] disabled:opacity-30" data-testid={`move-up-${idx}`}>&#9650;</button>
                                <button onClick={() => moveDown(idx)} disabled={idx === priorities.length - 1} className="w-5 h-5 text-[10px] rounded border border-[#d2d2d7] text-[#86868b] hover:text-[#1d1d1f] disabled:opacity-30" data-testid={`move-down-${idx}`}>&#9660;</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    {countyCompanies.map((co) => {
                      const isSelected = priorities.includes(co.id);
                      const rank = isSelected ? priorities.indexOf(co.id) + 1 : null;
                      return (
                        <button
                          key={co.id}
                          onClick={() => toggleCompany(co.id)}
                          className={`w-full flex items-center gap-3 p-4 rounded-xl text-left transition-all ${
                            isSelected
                              ? 'bg-blue-50 border-2 border-[#0071e3]'
                              : 'bg-[#f5f5f7] border-2 border-transparent hover:border-[#d2d2d7]'
                          }`}
                          data-testid={`company-${co.id}`}
                        >
                          {isSelected ? (
                            <span className="w-8 h-8 rounded-full bg-[#0071e3] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{rank}</span>
                          ) : (
                            <span className="w-8 h-8 rounded-full border-2 border-[#d2d2d7] flex items-center justify-center flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-[#1d1d1f]">{co.name}</h4>
                            <p className="text-xs text-[#86868b]">{co.sector}</p>
                            {co.description && <p className="text-xs text-[#86868b] mt-0.5 line-clamp-1">{co.description}</p>}
                          </div>
                          {co.openings > 0 && (
                            <span className="flex items-center gap-1 text-xs text-[#34C759] bg-green-50 px-2 py-1 rounded-full flex-shrink-0">
                              <Briefcase className="w-3 h-3" /> {co.openings}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* ===================== SAVE BAR ===================== */}
        <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-[#1d1d1f] flex items-center gap-2">
                Ready to continue?
                {discoveryHasSelection && prefsHasSelection && <Check className="w-5 h-5 text-green-600" />}
              </h3>
              <p className="text-sm text-[#86868b]">
                {selectedIndustries.length} industries, {selectedRoles.length} roles,
                {selectedCounty ? ` ${selectedCounty},` : ' no county yet,'} {priorities.length}/5 employers ranked
              </p>
            </div>
            <Button
              onClick={handleSaveAll}
              disabled={submitting || !discoveryHasSelection || !prefsHasSelection}
              className="bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full px-8 w-full sm:w-auto"
              data-testid="save-career-preferences-btn"
            >
              <Bookmark className="w-4 h-4 mr-2" />
              {submitting ? 'Saving...' : 'Save & Continue to Aptitude Tests'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareerPreferences;
